import React, { useState, useEffect } from 'react';
import { XMarkIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { adminApiService as apiService } from '../../services/adminApiService';
import { formatCurrency } from '../../utils/formatters';
import { ApiError } from '../../types/errorTypes';

interface BusinessRule {
  report_type: string;
  commission_tiers: CommissionTier[];
  fees: {
    manager_fee_percent: number;
    company_overhead_percent: number;
  };
}

interface CommissionTier {
  min_margin: number;
  max_margin: number;
  commission_percent: number;
}

interface RuleChanges {
  commission_tiers?: CommissionTier[];
  fees?: {
    manager_fee_percent?: number;
    company_overhead_percent?: number;
  };
}

interface ImpactSummary {
  affected_reports: number;
  sample_impacts: SampleImpact[];
  summary: {
    total_commission_delta: number;
    total_net_profit_delta: number;
    average_commission_change_percent: number;
  };
}

interface SampleImpact {
  report_id: number;
  report_name: string;
  current_total_commission: number;
  new_total_commission?: number;
  commission_delta?: number;
  job_impacts?: JobImpact[];
}

interface JobImpact {
  job_number: string;
  contract_total: number;
  gross_margin: number;
  current_commission: number;
  new_commission: number;
  commission_delta: number;
  tier_changed: boolean;
}

interface RuleChangeImpactAnalyzerProps {
  reportType: 'exteriors' | 'cody';
  currentRules: BusinessRule;
  proposedChanges: RuleChanges;
  onClose: () => void;
  onApply: (regenerate: boolean) => void;
}

const RuleChangeImpactAnalyzer: React.FC<RuleChangeImpactAnalyzerProps> = ({
  reportType,
  currentRules,
  proposedChanges,
  onClose,
  onApply
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [impactData, setImpactData] = useState<ImpactSummary | null>(null);
  const [sampleSize, setSampleSize] = useState(10);
  const [regenerateReports, setRegenerateReports] = useState(false);

  useEffect(() => {
    fetchImpactAnalysis();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleSize]);

  const fetchImpactAnalysis = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.post('/v2/admin/business-rules/preview-impact', {
        report_type: reportType,
        changes: proposedChanges,
        sample_size: sampleSize
      });
      
      setImpactData(response.data);
    } catch (err) {
      const error = err as ApiError;
      setError(error.response?.data?.message || 'Failed to fetch impact analysis');
    } finally {
      setLoading(false);
    }
  };

  const getDeltaDisplay = (value: number, isCurrency = true) => {
    if (value === 0) return <span className="text-gray-500">No change</span>;
    
    const formatted = isCurrency ? formatCurrency(Math.abs(value)) : `${Math.abs(value).toFixed(2)}%`;
    const isPositive = value > 0;
    
    return (
      <span className={`flex items-center ${isPositive ? 'text-red-600' : 'text-green-600'}`}>
        {isPositive ? <ArrowUpIcon className="h-3 w-3 mr-1" /> : <ArrowDownIcon className="h-3 w-3 mr-1" />}
        {isPositive ? '+' : '-'}{formatted}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Rule Change Impact Analysis</h2>
            <p className="mt-1 text-gray-600">Preview how changes will affect existing reports</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading && (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 text-red-800 rounded-lg">
              <p className="font-semibold">Error:</p>
              <p>{error}</p>
            </div>
          )}

          {impactData && !loading && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-600">Affected Reports</h4>
                  <p className="mt-1 text-2xl font-bold text-gray-900">{impactData.affected_reports}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-600">Total Commission Change</h4>
                  <div className="mt-1 text-2xl font-bold">
                    {getDeltaDisplay(impactData.summary.total_commission_delta)}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-600">Average Change</h4>
                  <div className="mt-1 text-2xl font-bold">
                    {getDeltaDisplay(impactData.summary.average_commission_change_percent, false)}
                  </div>
                </div>
              </div>

              {/* Change Summary */}
              <div className="bg-yellow-50 rounded-lg p-4">
                <h3 className="font-semibold text-yellow-800 mb-2">Proposed Changes:</h3>
                <ul className="text-sm text-yellow-700 space-y-1">
                  {proposedChanges.commission_tiers && (
                    <li>• Commission tier structure will be modified</li>
                  )}
                  {proposedChanges.fees?.manager_fee_percent !== undefined && (
                    <li>• Manager fee will change from {currentRules.fees.manager_fee_percent}% to {proposedChanges.fees.manager_fee_percent}%</li>
                  )}
                  {proposedChanges.fees?.company_overhead_percent !== undefined && (
                    <li>• Company overhead will change from {currentRules.fees.company_overhead_percent}% to {proposedChanges.fees.company_overhead_percent}%</li>
                  )}
                </ul>
              </div>

              {/* Warning for significant changes */}
              {Math.abs(impactData.summary.total_commission_delta) > 10000 && (
                <div className="bg-red-50 rounded-lg p-4">
                  <h3 className="font-semibold text-red-800 mb-2">⚠️ Significant Impact Detected</h3>
                  <p className="text-sm text-red-700">
                    This change will result in a {formatCurrency(Math.abs(impactData.summary.total_commission_delta))} 
                    {impactData.summary.total_commission_delta > 0 ? ' increase' : ' decrease'} in total commissions 
                    across the sampled reports. Please review carefully before applying.
                  </p>
                </div>
              )}

              {/* Sample Size Control */}
              <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Sample Size</label>
                  <p className="text-xs text-gray-500">Number of recent reports to analyze</p>
                </div>
                <select
                  value={sampleSize}
                  onChange={(e) => setSampleSize(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={10}>10 reports</option>
                  <option value={25}>25 reports</option>
                  <option value={50}>50 reports</option>
                  <option value={100}>100 reports</option>
                </select>
              </div>

              {/* Detailed Report Impacts */}
              {impactData.sample_impacts.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Sample Report Impacts</h3>
                  <div className="space-y-3">
                    {impactData.sample_impacts.slice(0, 5).map((impact) => (
                      <div key={impact.report_id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-medium text-gray-900">{impact.report_name}</h4>
                            <p className="text-sm text-gray-600">Report ID: {impact.report_id}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">Current Commission</p>
                            <p className="font-medium">{formatCurrency(impact.current_total_commission)}</p>
                          </div>
                        </div>
                        
                        {impact.job_impacts && impact.job_impacts.length > 0 && (
                          <div className="mt-3 bg-gray-50 rounded p-3">
                            <p className="text-xs font-medium text-gray-700 mb-2">Jobs with tier changes:</p>
                            <div className="space-y-1">
                              {impact.job_impacts
                                .filter(job => job.tier_changed)
                                .slice(0, 3)
                                .map((job, idx) => (
                                  <div key={idx} className="text-xs text-gray-600">
                                    Job {job.job_number}: {job.gross_margin.toFixed(1)}% margin, 
                                    commission {getDeltaDisplay(job.commission_delta)}
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="regenerate"
                checked={regenerateReports}
                onChange={(e) => setRegenerateReports(e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="regenerate" className="ml-2 text-sm text-gray-700">
                Regenerate all affected reports with new rules
              </label>
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => onApply(regenerateReports)}
                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Apply Changes
              </button>
            </div>
          </div>
          
          {regenerateReports && (
            <p className="mt-2 text-xs text-gray-500">
              Note: Regenerating reports may take several minutes depending on the number of affected reports.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RuleChangeImpactAnalyzer;